import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import api from '../lib/api';
import { toast } from 'react-hot-toast';
import { 
  Calendar, 
  CreditCard, 
  Users, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Play,
  Pause,
  RefreshCw,
  Eye,
  Settings,
  Search,
  Filter
} from 'lucide-react';

function SubscriptionManagementPage() {
  const queryClient = useQueryClient();
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showExtendTrialModal, setShowExtendTrialModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch businesses with subscription details
  const { data: businesses, isLoading, error } = useQuery({
    queryKey: ['businesses'],
    queryFn: async () => {
      const response = await api.get('/admin/businesses');
      return response.data.data; // Backend returns { success, data: { businesses: [...], pagination: {...} } }
    }
  });

  // Fetch available plans
  const { data: plans } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const response = await api.get('/admin/plans');
      return response.data.data; // Backend returns { success, data: [...] }
    }
  });

  // Extend trial mutation
  const extendTrialMutation = useMutation({
    mutationFn: ({ businessId, additionalDays }) => 
      api.post(`/businesses/${businessId}/extend-trial`, { additionalDays }),
    onSuccess: () => {
      toast.success('Trial extended successfully!');
      queryClient.invalidateQueries(['businesses']);
      setShowExtendTrialModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to extend trial');
    }
  });

  // Change plan mutation
  const changePlanMutation = useMutation({
    mutationFn: ({ businessId, planId }) => 
      api.post(`/businesses/${businessId}/change-plan`, { planId }),
    onSuccess: () => {
      toast.success('Plan changed successfully!');
      queryClient.invalidateQueries(['businesses']);
      setShowChangePlanModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to change plan');
    }
  });

  // Suspend/activate business mutation
  const toggleBusinessMutation = useMutation({
    mutationFn: ({ businessId, status }) => 
      api.post(`/businesses/${businessId}/suspend`, { status }),
    onSuccess: () => {
      toast.success(`Business ${selectedBusiness?.status === 'active' ? 'suspended' : 'activated'} successfully!`);
      queryClient.invalidateQueries(['businesses']);
      setShowSuspendModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update business status');
    }
  });

  // Force logout mutation
  const forceLogoutMutation = useMutation({
    mutationFn: (businessId) => 
      api.post(`/businesses/${businessId}/force-logout`),
    onSuccess: () => {
      toast.success('All users have been logged out successfully!');
      queryClient.invalidateQueries(['businesses']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to logout users');
    }
  });

  // Filter businesses
  const businessList = businesses?.businesses || [];
  const filteredBusinesses = businessList.filter(business => {
    const matchesSearch = business.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         business.subdomain.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || business.subscription_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleExtendTrial = (business) => {
    setSelectedBusiness(business);
    setShowExtendTrialModal(true);
  };

  const handleChangePlan = (business) => {
    setSelectedBusiness(business);
    setShowChangePlanModal(true);
  };

  const handleSuspendBusiness = (business) => {
    setSelectedBusiness(business);
    setShowSuspendModal(true);
  };

  const handleForceLogout = (business) => {
    if (window.confirm(`Are you sure you want to force logout all users for ${business.name}?`)) {
      forceLogoutMutation.mutate(business.id);
    }
  };

  if (isLoading) return <div className="p-6 text-center">Loading subscriptions...</div>;
  if (error) return <div className="p-6 text-center text-red-500">Error: {error.message}</div>;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Subscription Management</h1>
        <p className="text-gray-600">Manage business subscriptions, trials, and user sessions</p>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search businesses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="trialing">Trialing</option>
            <option value="past_due">Past Due</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Subscription Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Subscriptions</p>
              <p className="text-2xl font-bold text-green-600">
                {businessList.filter(b => b.subscription_status === 'active').length || 0}
              </p>
            </div>
            <CheckCircle className="text-green-500" size={24} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Trial Subscriptions</p>
              <p className="text-2xl font-bold text-blue-600">
                {businessList.filter(b => b.subscription_status === 'trialing').length || 0}
              </p>
            </div>
            <Clock className="text-blue-500" size={24} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Past Due</p>
              <p className="text-2xl font-bold text-yellow-600">
                {businessList.filter(b => b.subscription_status === 'past_due').length || 0}
              </p>
            </div>
            <AlertTriangle className="text-yellow-500" size={24} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cancelled</p>
              <p className="text-2xl font-bold text-red-600">
                {businessList.filter(b => b.subscription_status === 'cancelled').length || 0}
              </p>
            </div>
            <XCircle className="text-red-500" size={24} />
          </div>
        </div>
      </div>

      {/* Businesses Table */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Business Subscriptions</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Business
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscription Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trial End
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBusinesses?.map((business) => (
                <tr key={business.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{business.name}</div>
                      <div className="text-sm text-gray-500">{business.subdomain}.easica.com</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      business.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                      business.subscription_status === 'trialing' ? 'bg-blue-100 text-blue-800' :
                      business.subscription_status === 'past_due' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {business.subscription_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {business.plan}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {business.subscription_end_date ? 
                      new Date(business.subscription_end_date).toLocaleDateString() : 
                      'N/A'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {business.total_users}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleExtendTrial(business)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Extend Trial"
                      >
                        <Calendar size={16} />
                      </button>
                      <button
                        onClick={() => handleChangePlan(business)}
                        className="text-green-600 hover:text-green-900"
                        title="Change Plan"
                      >
                        <CreditCard size={16} />
                      </button>
                      <button
                        onClick={() => handleSuspendBusiness(business)}
                        className={`${business.status === 'active' ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}
                        title={business.status === 'active' ? 'Suspend' : 'Activate'}
                      >
                        {business.status === 'active' ? <Pause size={16} /> : <Play size={16} />}
                      </button>
                      <button
                        onClick={() => handleForceLogout(business)}
                        className="text-red-600 hover:text-red-900"
                        title="Force Logout All Users"
                      >
                        <RefreshCw size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredBusinesses?.length === 0 && (
          <div className="p-6 text-center text-gray-500">
            No businesses found matching your criteria.
          </div>
        )}
      </div>

      {/* Extend Trial Modal */}
      {showExtendTrialModal && (
        <ExtendTrialModal
          business={selectedBusiness}
          onClose={() => setShowExtendTrialModal(false)}
          onConfirm={(additionalDays) => extendTrialMutation.mutate({
            businessId: selectedBusiness.id,
            additionalDays
          })}
          isLoading={extendTrialMutation.isLoading}
        />
      )}

      {/* Change Plan Modal */}
      {showChangePlanModal && (
        <ChangePlanModal
          business={selectedBusiness}
          plans={plans}
          onClose={() => setShowChangePlanModal(false)}
          onConfirm={(planId) => changePlanMutation.mutate({
            businessId: selectedBusiness.id,
            planId
          })}
          isLoading={changePlanMutation.isLoading}
        />
      )}

      {/* Suspend Business Modal */}
      {showSuspendModal && (
        <SuspendBusinessModal
          business={selectedBusiness}
          onClose={() => setShowSuspendModal(false)}
          onConfirm={(status) => toggleBusinessMutation.mutate({
            businessId: selectedBusiness.id,
            status
          })}
          isLoading={toggleBusinessMutation.isLoading}
        />
      )}
    </div>
  );
}

// Extend Trial Modal Component
function ExtendTrialModal({ business, onClose, onConfirm, isLoading }) {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      additionalDays: 7
    }
  });

  const onSubmit = (data) => {
    onConfirm(parseInt(data.additionalDays));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Extend Trial</h2>
        <p className="text-gray-600 mb-4">
          Extend the trial period for <strong>{business?.name}</strong>
        </p>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <label htmlFor="additionalDays" className="block text-sm font-medium text-gray-700 mb-2">
              Additional Days
            </label>
            <input
              type="number"
              id="additionalDays"
              {...register('additionalDays', { 
                required: 'Additional days is required',
                min: { value: 1, message: 'Must be at least 1 day' },
                max: { value: 365, message: 'Cannot exceed 365 days' }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              min="1"
              max="365"
            />
            {errors.additionalDays && (
              <p className="text-red-500 text-sm mt-1">{errors.additionalDays.message}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Extending...' : 'Extend Trial'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Change Plan Modal Component
function ChangePlanModal({ business, plans, onClose, onConfirm, isLoading }) {
  const { register, handleSubmit } = useForm({
    defaultValues: {
      planId: business?.current_plan_id || ''
    }
  });

  const onSubmit = (data) => {
    onConfirm(data.planId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Change Plan</h2>
        <p className="text-gray-600 mb-4">
          Change the subscription plan for <strong>{business?.name}</strong>
        </p>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <label htmlFor="planId" className="block text-sm font-medium text-gray-700 mb-2">
              New Plan
            </label>
            <select
              id="planId"
              {...register('planId', { required: 'Plan selection is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a plan</option>
              {plans?.map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} - ${plan.price}/month
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Changing...' : 'Change Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Suspend Business Modal Component
function SuspendBusinessModal({ business, onClose, onConfirm, isLoading }) {
  const isActive = business?.status === 'active';
  const actionText = isActive ? 'Suspend' : 'Activate';
  const actionColor = isActive ? 'red' : 'green';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-gray-800 mb-4">{actionText} Business</h2>
        <p className="text-gray-600 mb-4">
          Are you sure you want to {actionText.toLowerCase()} <strong>{business?.name}</strong>?
        </p>
        {isActive && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-700">
              <strong>Warning:</strong> Suspending this business will prevent all users from accessing the platform.
            </p>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(isActive ? 'suspended' : 'active')}
            className={`px-4 py-2 bg-${actionColor}-600 text-white rounded-md hover:bg-${actionColor}-700 disabled:opacity-50`}
            disabled={isLoading}
          >
            {isLoading ? `${actionText}ing...` : actionText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionManagementPage;
